import { useState } from 'react'

import { QRGenerator } from '../components/QRGenerator'
import { Alert, Badge, Card, Tabs, Textarea, Button, Field } from '../components/ui'
import { ApiError, request } from '../lib/api'
import type { DecodeResult } from '../lib/types'

type Mode = 'generate' | 'decode'

export function Generator() {
  const [mode, setMode] = useState<Mode>('generate')

  return (
    <div className="container-page py-10">
      <header className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight">Генератор QR-кодів НБУ</h1>
        <p className="mt-2 max-w-2xl text-slate-600 dark:text-slate-400">
          Створіть платіжний QR або розберіть уже наявний на поля. Безкоштовно, без реєстрації.
        </p>
      </header>

      <div className="mb-6">
        <Tabs<Mode>
          value={mode}
          onChange={setMode}
          tabs={[
            { value: 'generate', label: 'Створити QR' },
            { value: 'decode', label: 'Розібрати QR' },
          ]}
        />
      </div>

      {mode === 'generate' ? <QRGenerator /> : <Decoder />}
    </div>
  )
}

function Decoder() {
  const [payload, setPayload] = useState('')
  const [result, setResult] = useState<DecodeResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const decode = async () => {
    setBusy(true)
    setError(null)
    setResult(null)
    try {
      setResult(
        await request<DecodeResult>('/playground/decode', {
          method: 'POST',
          auth: false,
          body: { payload },
        }),
      )
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не вдалося розібрати payload')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Card
        title="Вміст відсканованого QR"
        description="Вставте текст, який видав будь-який сканер QR-кодів."
      >
        <Field hint="Рядки розділяються переносом. Перший рядок має бути BCD.">
          {(id) => (
            <Textarea
              id={id}
              rows={12}
              value={payload}
              onChange={(e) => setPayload(e.target.value)}
              placeholder={
                'BCD\n002\n1\nUCT\n\nТОВ "Ромашка"\nUA213223130000026007233566001\n' +
                'UAH1250.00\n32129530\n\n\nОплата за рахунком №12/8'
              }
              className="font-mono text-xs"
            />
          )}
        </Field>
        <Button onClick={decode} loading={busy} disabled={!payload.trim()} className="mt-4">
          Розібрати
        </Button>
      </Card>

      <div className="flex flex-col gap-4">
        {error && <Alert tone="danger">{error}</Alert>}

        {result && (
          <>
            <Alert tone={result.valid ? 'success' : 'warning'}>
              {result.valid
                ? `Валідний QR стандарту ${result.version.toUpperCase()}.`
                : `Розібрано як ${result.version.toUpperCase()}, але є зауваження.`}
            </Alert>

            {result.issues.length > 0 && (
              <Card title="Зауваження">
                <ul className="space-y-2 text-sm">
                  {result.issues.map((issue) => (
                    <li key={issue} className="flex gap-2">
                      <span className="text-amber-500">•</span>
                      <span className="text-slate-600 dark:text-slate-300">{issue}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            <Card title="Розібрані поля" padded={false}>
              <dl className="divide-y divide-slate-200 text-sm dark:divide-slate-800">
                {Object.entries(result.fields)
                  .filter(([, value]) => value !== '')
                  .map(([key, value]) => (
                    <div key={key} className="flex gap-4 px-5 py-2.5">
                      <dt className="w-44 shrink-0 font-mono text-xs text-slate-500 dark:text-slate-400">
                        {key}
                      </dt>
                      <dd className="break-all font-medium">{value}</dd>
                    </div>
                  ))}
              </dl>
            </Card>

            {result.amount && (
              <Badge tone="brand" className="self-start text-sm">
                Сума: {result.amount} UAH
              </Badge>
            )}

            {result.lockedFields.length > 0 && (
              <Card title="Заборонено редагувати">
                <p className="mb-3 text-sm text-slate-600 dark:text-slate-400">
                  Емітент цього QR заблокував зміну цих полів — платник не зможе їх
                  виправити у застосунку банку.
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {result.lockedFields.map((name) => (
                    <Badge key={name} tone="warning">
                      {name}
                    </Badge>
                  ))}
                </div>
              </Card>
            )}
          </>
        )}

        {!result && !error && (
          <Card>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Тут зʼявиться розбір payload на поля стандарту з переліком проблем — зручно,
              коли банк відмовляється читати чужий QR.
            </p>
          </Card>
        )}
      </div>
    </div>
  )
}
